import React, { useEffect, useState } from 'react';
import ExportVariantIcon from 'mdi-react/ExportVariantIcon';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';

import QuestionsStatsModel from './QuestionsStatsModel';
import { getQuestionType } from './QuestionnaireBuilder/QuestionType';
import Api from '../../../util/api';
import Loading from '../../shared/Spinner';

import t, { partial } from '../../../util/translation/translation';

const RECENT_TEXT_ANSWERS = 5;

const isAnswered = (answer) =>
  answer !== undefined &&
  answer !== null &&
  answer !== '' &&
  !(Array.isArray(answer) && answer.length === 0);

const percentOf = (value, total) =>
  total ? Math.round((value / total) * 100) : 0;

// Per choice: how many picked it. Checkbox answers are arrays (several
// answers allowed), dropdown and radio answers a single string.
const countChoices = (values) => {
  const labels = [];
  const count = {};
  values.forEach((value) => {
    value?.choices?.forEach(({ choice }) => {
      if (!labels.includes(choice)) labels.push(choice);
    });
    const picked = Array.isArray(value.answer) ? value.answer : [value.answer];
    picked.filter(Boolean).forEach((answer) => {
      count[answer] = (count[answer] || 0) + 1;
      if (!labels.includes(answer)) labels.push(answer);
    });
  });
  return labels.map((label) => ({ label, count: count[label] || 0 }));
};

// Per reservation spot: seats booked (a booking can hold several seats)
const countBookings = (values) => {
  const options = values.find((v) => v?.options?.length)?.options || [];
  const booked = {};
  values.forEach(({ answer }) => {
    let selectedId = null;
    let qty = 1;
    if (answer && typeof answer === 'object' && answer.selectedItem) {
      selectedId = answer.selectedItem;
      qty = answer.quantity || 1;
    } else if (typeof answer === 'string' && answer !== '') {
      selectedId = answer;
    }
    if (selectedId) booked[selectedId] = (booked[selectedId] || 0) + qty;
  });
  return options.map((opt) => ({
    label: opt.title,
    booked: booked[opt.id] || 0,
    capacity: opt.capacity || 0,
  }));
};

const QuestionsStatsCard = ({ eventId }) => {
  const [totalResponses, setTotalResponses] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [groupedAnswers, setGroupedAnswers] = useState({});
  const [questionNumber, setQuestionNumber] = useState(0);
  const [questionKey, setQuestionKey] = useState('');
  const [questionValues, setQuestionValues] = useState([]);
  const qS = partial('question');

  const groupAnswers = async () => {
    setIsLoading(true);
    try {
      const answers = await Api.questions.fetchAnswers(eventId);
      const submissions = answers?.data || [];
      setTotalResponses(submissions.length);
      // Participants' own answers, grouped by question (guests excluded)
      const grouped = {};
      submissions.forEach((answerObj) => {
        answerObj?.answer?.forEach((answer) => {
          if (answer?.guest === true) return;
          if (!grouped[answer.question]) grouped[answer.question] = [];
          grouped[answer.question].push(answer);
        });
      });
      setGroupedAnswers(grouped);
    } catch {
      toast.error(t('shared.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    groupAnswers();
    return () => {
      setGroupedAnswers({});
      setTotalResponses(0);
    };
    // eslint-disable-next-line
  }, []);

  const handleCloseModal = () => {
    setQuestionValues([]);
    setQuestionKey('');
    setShowModal(false);
  };

  const handleCSVExport = async () => {
    try {
      const [answersRes, participants, companiesRes] = await Promise.all([
        Api.questions.fetchAnswers(eventId),
        Api.events.getParticipants(eventId),
        Api.companies.getCompanies(10000, 1, '', 'name', false, true, true, ''),
      ]);

      const answersData = answersRes?.data || [];

      // Build company ID → name lookup
      const companyNameMap = {};
      const companiesData = companiesRes?.data?.data || [];
      companiesData.forEach((c) => {
        companyNameMap[c.id] = c.name;
      });

      // Build userId → participant lookup
      // Participants API returns a map: { GUEST: [...], USER: [...], companyId: {...} }
      const participantsMap = participants?.data || {};
      const participantLookup = {};
      Object.keys(participantsMap).forEach((key) => {
        if (key === 'GUEST' || key === 'USER') {
          (participantsMap[key] || []).forEach((p) => {
            participantLookup[p.userId] = {
              ...p,
              companyName: companyNameMap[p.companyId] || '',
            };
          });
        } else {
          // Company participants — key is the company ID
          const companyName = companyNameMap[key] || key;
          const companyData = participantsMap[key];
          if (companyData?.participants) {
            companyData.participants.forEach((emp) => {
              participantLookup[emp.userId] = { ...emp, companyName };
            });
          }
        }
      });

      // Collect unique questions (with their options) from the first answer that has each one
      const questionMap = new Map();
      answersData.forEach((answerObj) => {
        answerObj?.answer?.forEach((a) => {
          if (a?.guest !== true && !questionMap.has(a.id)) {
            questionMap.set(a.id, a);
          }
        });
      });
      const questions = Array.from(questionMap.values());

      // Build question ID → options lookup from answer entries
      const questionOptionsMap = {};
      questions.forEach((q) => {
        questionOptionsMap[q.id] = q.options || [];
      });

      // Shared translation for headers
      const s = partial('shared');

      // CSV header
      const headers = [
        s('firstname'),
        s('lastname'),
        s('phone'),
        s('email'),
        s('company'),
        s('guests'),
      ];
      questions.forEach((q) => headers.push(q.question));

      let csv = `${headers.join(';')}\n`;

      // Rows: one per unique answer submitter
      answersData.forEach((answerObj) => {
        const participant = participantLookup[answerObj.userId];
        const userAnswers = answerObj?.answer || [];

        // Build answer map: question id → answer entry (for options access)
        const answerEntryMap = {};
        userAnswers.forEach((a) => {
          if (a?.guest !== true) {
            answerEntryMap[a.id] = a;
          }
        });

        const row = [
          String(participant?.firstname || '').replace(/;/g, ' '),
          String(participant?.lastname || '').replace(/;/g, ' '),
          String(participant?.phoneNumber || '').replace(/;/g, ' '),
          String(participant?.email || '').replace(/;/g, ' '),
          String(
            participant?.company?.name || participant?.companyName || ''
          ).replace(/;/g, ' '),
          participant?.friendAmount || 0,
        ];

        questions.forEach((q) => {
          const entry = answerEntryMap[q.id];
          let val = entry?.answer;

          if (val === undefined || val === '') {
            val = '';
          } else if (q.question_type === 'reservation') {
            // answer format: { selectedItem: "uuid", quantity: N } or plain string
            const options = questionOptionsMap[q.id] || [];
            let itemId;
            if (typeof val === 'object' && val.selectedItem) {
              itemId = val.selectedItem;
              const option = options.find((o) => o.id === itemId);
              const room = option ? option.title : itemId;
              const qty = val.quantity || 1;
              val = `${room} (×${qty})`;
            } else if (typeof val === 'string') {
              const option = options.find((o) => o.id === val);
              val = option ? option.title : val;
            } else {
              val = JSON.stringify(val);
            }
          } else if (Array.isArray(val)) {
            // checkbox answer: ['choice1', 'choice2']
            val = val.join(', ');
          } else if (typeof val === 'object') {
            val = JSON.stringify(val);
          }
          row.push(String(val).replace(/;/g, ' ').replace(/\n/g, ' '));
        });

        csv += `${row.join(';')}\n`;
      });

      fileDownload(`﻿${csv}`, `event-questions-${eventId}.csv`);
    } catch (e) {
      console.error(e);
      toast.error(t('shared.somethingWentWrong'));
    }
  };
  const entries = Object.entries(groupedAnswers);
  const answerSlots = entries.reduce(
    (sum, [, values]) => sum + values.length,
    0
  );
  const answeredSlots = entries.reduce(
    (sum, [, values]) =>
      sum + values.filter((v) => isAnswered(v.answer)).length,
    0
  );

  const renderBar = (key, label, value, percent, full = false) => (
    <div className="qs-bar-row" key={key}>
      <div className="qs-bar-row__head">
        <span>{label}</span>
        <span
          className={`qs-bar-row__value${full ? ' qs-bar-row__value--full' : ''}`}
        >
          {value}
        </span>
      </div>
      <div className="qs-bar">
        <div
          className={`qs-bar__fill${full ? ' qs-bar__fill--full' : ''}`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
    </div>
  );

  const renderChoices = (values) => {
    const answered = values.filter((v) => isAnswered(v.answer)).length;
    return countChoices(values).map(({ label, count }) => {
      const percent = percentOf(count, answered);
      return renderBar(label, label, `${count} · ${percent}%`, percent);
    });
  };

  const renderReservation = (values) =>
    countBookings(values).map(({ label, booked, capacity }) => {
      const full = capacity > 0 && booked >= capacity;
      return renderBar(
        label,
        label,
        full
          ? qS('spotFull', { booked, capacity })
          : qS('spotBooked', { booked, capacity }),
        percentOf(booked, capacity),
        full
      );
    });

  const renderText = (key, values, number) => {
    const answered = values.filter((v) => isAnswered(v.answer));
    return (
      <>
        <ul className="qs-text-answers">
          {answered.slice(0, RECENT_TEXT_ANSWERS).map((item, index) => (
            <li key={index}>{String(item.answer)}</li>
          ))}
        </ul>
        {answered.length > RECENT_TEXT_ANSWERS && (
          <button
            type="button"
            className="qs-link"
            onClick={() => {
              setQuestionNumber(number);
              setQuestionValues(values);
              setQuestionKey(key);
              setShowModal(true);
            }}
          >
            {qS('viewAllResponses')}
          </button>
        )}
      </>
    );
  };

  const renderContent = (key, values, number) => {
    switch (values[0]?.question_type) {
      case 'text_field':
        return renderText(key, values, number);
      case 'dropdown':
      case 'radio_button':
      case 'checkbox':
        return renderChoices(values);
      case 'reservation':
        return renderReservation(values);
      default:
        return null;
    }
  };

  const subLine = (type, answered, total) => {
    if (type === 'reservation') return qS('seatsBookedOfCapacity');
    const base = qS('answeredOf', { answered, total });
    return type === 'checkbox' ? `${base} · ${qS('severalAllowed')}` : base;
  };

  return (
    <div className="qs">
      <div className="qs__top">
        <div className="participant-stats qs__tiles">
          <div className="participant-stats__item">
            <span className="participant-stats__label">
              {qS('totalResponses')}
            </span>
            <span className="participant-stats__value">{totalResponses}</span>
            <span className="qs__tile-sub">{qS('responsesSub')}</span>
          </div>
          <div className="participant-stats__item">
            <span className="participant-stats__label">{qS('answerRate')}</span>
            <span className="participant-stats__value">
              {percentOf(answeredSlots, answerSlots)}%
            </span>
            <span className="qs__tile-sub">{qS('answerRateSub')}</span>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-0 qs__export"
          onClick={handleCSVExport}
          disabled={!totalResponses}
        >
          <ExportVariantIcon size={16} />
          {qS('exportCSV')}
        </button>
      </div>

      {isLoading && <Loading />}
      {!isLoading && !entries.length && (
        <div className="qs__empty">{qS('noResponses')}</div>
      )}
      {!isLoading && entries.length > 0 && (
        <div className="qs__grid">
          {entries.map(([key, values], index) => {
            const type = values[0]?.question_type;
            const meta = getQuestionType(type);
            const answered = values.filter((v) => isAnswered(v.answer)).length;
            return (
              <section className="qs-card" key={`question-${key}`}>
                <div className="qs-card__head">
                  <div>
                    <h4 className="qs-card__title">{`${index + 1}. ${key}`}</h4>
                    <span className="qs-card__sub">
                      {subLine(type, answered, values.length)}
                    </span>
                  </div>
                  {meta && <span className="qb-chip">{meta.label}</span>}
                </div>
                <div className="qs-card__body">
                  {renderContent(key, values, index + 1)}
                </div>
              </section>
            );
          })}
        </div>
      )}
      <QuestionsStatsModel
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        questionNumber={questionNumber}
        questionKey={questionKey}
        questionValues={questionValues}
      />
    </div>
  );
};

export default QuestionsStatsCard;
